import { Injectable } from "@angular/core";
import { FileUploadService } from "./submission/file-upload.service";
import { ModelBackendService } from "./model-backend.service";
import { NamedBlob } from "./dicom-common";
import { CdssBodyRegion, CdssModel } from "./submission/model";
import { catchError, concat, concatMap, concatWith, defer, forkJoin, from, map, mergeMap, Observable, of, switchMap, tap, throwError, toArray } from "rxjs";
import { DicomService, FormatDynamicStringCb } from "./dicom.service";
import { isGzipFile } from "./nifti";
import { CdssAllFeatures, CdssPatientData, OriginalSeriesData } from "./report/cdss-report.service";
import { DicomTags } from "./dicom-writer/dicom-writer";
import { mergeJSONTags } from "./dicom-writer/dicom-vr";
import { HttpErrorResponse } from "@angular/common/http";
import * as base64 from 'base64-arraybuffer';
import { DeepLearningService } from "./deep-learning.service";
import { DummyTempDirFilter, NodeTempDirFilter, TempDirFilter } from "./node-temp-dir";
import { createHandlebars } from "./report/handlebars";

const maxDownloadConcurrency = 10;

@Injectable({
  providedIn: 'any'
})
export class DicomSCExportService {
	private readonly handlebars = createHandlebars();

	constructor(private fileUploadBackend: FileUploadService, private modelBackend: ModelBackendService, private dicomService: DicomService,
		private dlService: DeepLearningService,
	) { }

	public getExportableRegions(cdssApp: CdssModel | string): Observable<CdssBodyRegion[]> {
		let cdssAppObs: Observable<CdssModel>;
		if (typeof cdssApp === "string")
			cdssAppObs = this.modelBackend.get(cdssApp);
		else
			cdssAppObs = of(cdssApp);

		return cdssAppObs.pipe(
			map(app => {
				return app.data.bodyRegions.filter(r => r.secondaryCapture);
			})
		);
	}

	// Expects either a pre-fetched CdssModel or its ID
	// The returned observable will emit multiple times, once for each region!
	// For DICOM outputs (meaning DICOMs produced on cloud side, i.e. not needing conversion from Nifti),
	// prefix the region ID with "dicomoutput," so that it is unique and does not clash with Nifti-based regions.
	public exportAllDicomSC(batchId: string, cdssApp: CdssModel | string, originalTags: CdssPatientData,
		uniqueSubmissionId: string, onlyRegions?: Set<string>, dynamicStringContext?: DynamicStringContext): Observable<NamedBlob[]> {

		let cdssAppObs: Observable<CdssModel>;
		if (typeof cdssApp === "string")
			cdssAppObs = this.modelBackend.get(cdssApp);
		else
			cdssAppObs = of(cdssApp);
	
		const batchFilesObs = this.fileUploadBackend.listBatchFiles(batchId).pipe(catchError(err => {
			if (err instanceof HttpErrorResponse && err.status === 404) {
				console.error("Batch not found: " + batchId, err);
				return throwError(() => new BatchRemovedError(err));
			}

			// Rethrow original error
			return throwError(() => err);
		}));

		const formatDynamicString = (formatString: string, index: number): string => {
			if (dynamicStringContext) {
				const template = this.handlebars.compile(formatString);
				const withIndex: DynamicStringContextInternal = { ...dynamicStringContext, index };
				return template(withIndex);
			} else {
				console.warn("No dynamic string context provided, cannot format dynamic string: " + formatString);
				return formatString;
			}
		};

		const isNode = typeof process !== 'undefined' && process.versions?.node;
		let tempDirFilter: TempDirFilter;
	
		return forkJoin([cdssAppObs, batchFilesObs]).pipe(
			switchMap(([cdssApp, batchFiles]) => {
				let exportingDicomOutputs = false;
				if (cdssApp.exportedDicomOutputs?.length > 0) { // If the app is exporting any DICOM outputs
					// And we either export all or we export some of the DICOM outputs
					if (!onlyRegions || [...onlyRegions].find(r => r.startsWith("dicomoutput,"))) {
						exportingDicomOutputs = true;
					}
				}

				if (exportingDicomOutputs) {
					// Then we need to fetch the details of these exports from pipeline definition
					return this.dlService.getDefinedPipelines().pipe(
						map(pipelines => pipelines.find(p => p.id === cdssApp.dlPipeline)),
						map(pipeline => [cdssApp, batchFiles, pipeline] as const)
					);
				}

				// No pipeline definition is needed, provide a null
				return of([cdssApp, batchFiles, null] as const);
			}),
			mergeMap(([cdssApp, batchFiles, pipeline]) => {
				const regionsToExport = cdssApp.data.bodyRegions.filter(r => r.secondaryCapture && (!onlyRegions || onlyRegions.has(r.id)));
	
				// Create an array of regions to process
				const tasks: { region: CdssBodyRegion, niftiFile: string, index: number, total: number }[] = [];
				const dicomOriginTasks: { dicomFiles: string[] }[] = [];

				if (pipeline) {
					// The previous pipe() step passed to us the pipeline definition,
					// that means we are exporting some DICOM outputs.
					// Let's gather them.

					for (const dicomOutputId of cdssApp.exportedDicomOutputs) {
						if (onlyRegions && !onlyRegions.has(`dicomoutput,${dicomOutputId}`)) {
							continue; // This DICOM output is not requested
						}

						const dicomOutput = pipeline.providedDicomOutputs.find(o => o.id === dicomOutputId);
						if (!dicomOutput) {
							// This may only happen if the pipeline was reconfigured since the CDSS app was created
							console.error("No DICOM output found for ID: " + dicomOutputId);
							continue;
						}

						const dicomOutputDirectory = dicomOutput.directory.replace(/\/+$/, ''); // Remove trailing slashes
						const dicomFiles = batchFiles.filter(f => f.startsWith(dicomOutputDirectory + "/"));

						dicomOriginTasks.push({ dicomFiles });
					}					
				}
	
				for (const region of regionsToExport) {
					if (!region.voi) {
						console.error("No pattern for matching region: " + region.id);
						continue;
					}
	
					const re = new RegExp(region.voi + "\\.nii(\\.gz)?$", "i");
					const niftiFiles = batchFiles.filter(f => f.match(re));
					if (niftiFiles.length === 0) {
						console.error("No Nifti files matching region: " + region.id);
						continue;
					}

					niftiFiles.sort(new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }).compare);

					for (let i = 0; i < niftiFiles.length; i++) {
						tasks.push({ region, niftiFile: niftiFiles[i], index: i, total: niftiFiles.length });
					}
				}
	
				// Process Nifti -> DICOM tasks one after another, emitting each result
				const tasksObs = from(tasks).pipe(
					concatMap(task =>
						this.fileUploadBackend.downloadFileNoProgress(batchId, task.niftiFile).pipe(
							mergeMap(niftiBlob => from(this.gunzip(niftiBlob))),
							mergeMap(unzippedBlob => {
								let seriesDescription = task.region.secondaryCapture.description || task.region.niceName || task.region.id;
								if (task.total > 1) {
									// This is the scenario where we have multiple Nifti files for the same region
									// and we produce multiple DICOM series for it.
									//
									// In this scenario, we handle dynamic series description formatting here
									// (and not in DicomConverter's C++ code).
									// If we have *one input Nifti* that gets split into *multiple DICOM outputs*,
									// this handling not done here - it is instead done in DicomConverter.
									if (seriesDescription.includes("{{")) {
										seriesDescription = formatDynamicString(seriesDescription, task.index);
									} else {
										seriesDescription += ` (#${task.index + 1})`;
									}
								}

								const conversion = async () => {
									const input = await this.niftiBlobFilter(unzippedBlob);

									const dicoms = await this.convertToTargetDicom(input, cdssApp, originalTags, task.region, seriesDescription, uniqueSubmissionId, formatDynamicString);

									await this.niftiBlobFilterCleanup(input);

									return dicoms;
								};
	
								return from(conversion()).pipe(map(dicomBlobs => {
									for (const dicomBlob of dicomBlobs) {
										const indexString = `${task.index + 1}`.padStart(3, '0');
										dicomBlob.name = `${task.region.id}-${indexString}/${dicomBlob.name}`;
									}
									return dicomBlobs;
								}));
							})
						)
					)
				);

				let originalPatientModuleData: ArrayBuffer | undefined = undefined;

				console.debug(`dicomOriginTasks.length: ${dicomOriginTasks.length}, originalPatientModuleData present: ${!!originalTags.originalPatientModuleData}`);

				// If we have any DICOM origin tasks, we need the original patient module data for deanonymization
				if (dicomOriginTasks.length > 0 && originalTags.originalPatientModuleData) {
					originalPatientModuleData = base64.decode(originalTags.originalPatientModuleData);
					tempDirFilter = isNode ? new NodeTempDirFilter() : new DummyTempDirFilter();
				} else {
					tempDirFilter = new DummyTempDirFilter();
				}

				const dicomOriginObs = from(dicomOriginTasks).pipe(
					tap(defer(() => tempDirFilter.initialize())),
					concatMap(task => {
						// Fetch all files from given subdirectory, but limit the number of parallel downloads
						// to maxDownloadConcurrency.
						return from(task.dicomFiles).pipe(
							mergeMap(dicomFile => 
								this.fileUploadBackend.downloadFileNoProgress(batchId, dicomFile).pipe(
									switchMap(dcmFile => defer(async () => {
										// Filter out non-DICOM files to avoid pushing garbage
										if (!await isDicomFile(dcmFile)) {
											return null;
										}

										// Associate a filename with the blob
										const namedBlob: NamedBlob = {
											data: dcmFile,
											name: dicomFile,
										};

										// In Node.js, tempDirFilter.process() moves the in-memory blob to a temp file on disk
										return await tempDirFilter.process(namedBlob);
									}))
								), maxDownloadConcurrency),
							toArray()
						).pipe(
							// Filter out skipped files from previous step
							map(series => series.filter(s => s !== null) as (NamedBlob[] | string[])),
							// If we have original patient module data, deanonymize the series.
							// (We really should have it, but just in case...)
							switchMap(series => {
								if (originalPatientModuleData) {
									return from(this.dicomService.deanonymizeDicoms(tempDirFilter.toWorker(series), originalPatientModuleData));
								}

								// Leave as is
								// In this case, series is definitely NamedBlob[], because tempDirFilter is DummyTempDirFilter.
								return of(series as NamedBlob[]);
							})
						);
					})
				);

				// Do Nifti -> DICOM tasks first, then continue with the originally DICOM outputs
				return tasksObs.pipe(
					concatWith(dicomOriginObs),
					tap(() => tempDirFilter.cleanup())
				);
			})
		);
	}

	private tagsForModality(bodyRegionId: string, bodyRegionName: string, modality: string,
		originalTags: CdssPatientData, seriesDescription: string, uniqueSubmissionId: string,
		segColor?: number[], originalSeries?: OriginalSeriesData, softwareVersions?: string[]): any[] {

		let tags: any[] = [];
		tags.push(
			DicomTags.Manufacturer.makeJSON("Telix Pharmaceuticals Limited"),
			DicomTags.StudyDate.makeJSON(originalTags.studyDate),
			DicomTags.StudyTime.makeJSON(),
			DicomTags.SeriesNumber.makeJSON(1),
			DicomTags.AccessionNumber.makeJSON(""),
			DicomTags.ReferringPhysicianName.makeJSON(""),
			DicomTags.PerformedProcedureStepID.makeJSON(uniqueSubmissionId),
		);

		if (softwareVersions) {
			for (const version of softwareVersions) {
				tags.push(
					DicomTags.SoftwareVersions.makeJSON(version),
				);
			}
		}

		switch (modality) {
			case "SEG":
				tags.push(
					DicomTags.StudyID.makeJSON(originalTags.studyId),
					DicomTags.StudyInstanceUID.makeJSON(originalTags.studyUid),
					DicomTags.FrameOfReferenceUID.makeJSON(originalSeries.frameOfReferenceUID ?? ""),
				);

				let segmentColor = RGBToLab(1, 0, 0); // default red
				if (segColor)
					segmentColor = RGBToLab(segColor[0], segColor[1], segColor[2]);

				// Required by OHIF Viewer
				// https://dicom.innolitics.com/ciods/rt-plan/common-instance-reference/00081115
				tags.push(
					DicomTags.ReferencedSeriesSequence.makeJSON(
						mergeJSONTags(
							DicomTags.ReferencedInstanceSequence.makeJSON(
								...originalSeries.instanceUids.map(instanceUid => 
									mergeJSONTags(
										DicomTags.ReferencedSOPClassUID.makeJSON(originalSeries.sopClass),
										DicomTags.ReferencedSOPInstanceUID.makeJSON(instanceUid),
									)
								)
							),
							DicomTags.SeriesInstanceUID.makeJSON(originalTags.studyUid ?? ""),
						)
					),
				);

				// Only one label per series
				tags.push(
					DicomTags.SegmentSequence.makeJSON(
						mergeJSONTags(
							DicomTags.SegmentNumber.makeJSON(1),
							DicomTags.SegmentLabel.makeJSON(seriesDescription),
							DicomTags.SegmentDescription.makeJSON(bodyRegionName),
							DicomTags.SegmentAlgorithmType.makeJSON("AUTOMATIC"),
							DicomTags.SegmentAlgorithmName.makeJSON("TELIX"),
							DicomTags.RecommendedDisplayCIELabValue.makeJSON(LabToDICOM(segmentColor[0], segmentColor[1], segmentColor[2])),
							DicomTags.SegmentedPropertyCategoryCodeSequence.makeJSON(
								mergeJSONTags(
									DicomTags.CodeValue.makeJSON("130046"),
									DicomTags.CodingSchemeDesignator.makeJSON("DCM"),
									DicomTags.CodeMeaning.makeJSON("Non-specific volume")
								)
							),
							DicomTags.SegmentedPropertyTypeCodeSequence.makeJSON(
								mergeJSONTags(
									DicomTags.CodeValue.makeJSON("T-D0050"),
									DicomTags.CodingSchemeDesignator.makeJSON("SRT"),
									DicomTags.CodeMeaning.makeJSON("Tissue")
								)
							)
						)
					)
				);

				// Nowadays we only do BINARY segmentation to keep things simple
				tags.push(
					DicomTags.SegmentationType.makeJSON("BINARY"),
				);
				break;
			case "RTSTRUCT":
				tags.push(
					DicomTags.StudyID.makeJSON(originalTags.studyId),
					DicomTags.StudyInstanceUID.makeJSON(originalTags.studyUid),
					DicomTags.SeriesDescription.makeJSON(bodyRegionName),

					DicomTags.StructureSetName.makeJSON(bodyRegionName),
					DicomTags.StructureSetLabel.makeJSON(bodyRegionId),
					DicomTags.StructureSetDate.makeJSON(new Date()),
					DicomTags.StructureSetTime.makeJSON(new Date()),

					DicomTags.OperatorsName.makeJSON(""),
				);

				tags.push(
					DicomTags.ReferencedFrameOfReferenceSequence.makeJSON(
						mergeJSONTags(
							DicomTags.FrameOfReferenceUID.makeJSON(originalSeries.frameOfReferenceUID ?? ""),
							DicomTags.RTReferencedStudySequence.makeJSON(
								mergeJSONTags(
									DicomTags.ReferencedSOPClassUID.makeJSON("1.2.840.10008.3.1.2.3.1"), // Detached Study Management SOP Class (Retired)
									DicomTags.ReferencedSOPInstanceUID.makeJSON(originalSeries.seriesUid),
									DicomTags.RTReferencedSeriesSequence.makeJSON(
										mergeJSONTags(
											DicomTags.SeriesInstanceUID.makeJSON(originalSeries.seriesUid),
											DicomTags.ContourImageSequence.makeJSON(
												...originalSeries.instanceUids.map(instanceUid => 
													mergeJSONTags(
														DicomTags.ReferencedSOPClassUID.makeJSON(originalSeries.sopClass),
														DicomTags.ReferencedSOPInstanceUID.makeJSON(instanceUid),
													)
												)
											)
										)
									)
								)
							)
						)
					)
				);

				break;
			default:
				console.error("Unsupported DICOM modality: " + modality);
				return [];
		}

		return tags;
	}

	// Optionally decompresses the blob if it is a Gzip file
	private async gunzip(blob: Blob): Promise<Blob> {
		if (isGzipFile(blob)) {
			const ds = new DecompressionStream("gzip");
			const decompressedStream = blob.stream().pipeThrough(ds);
			const response = new Response(decompressedStream);
			return await response.blob();
		}
		return blob;
	}

	// Special handling for Node.js (DICOM Node)
	private async niftiBlobFilter(blob: Blob): Promise<NamedBlob | string> {
		const isNode = typeof process !== 'undefined' && process.versions?.node;

		if (isNode) {
			// We need to save it to disk, because WORKERFS doesn't work in Node.js
			const fs = await import('fs');
			const path = await import('path');
			const os = await import('os');

			// Create a random temp dir
			const tmpDir = os.tmpdir(); // system temp directory
			const randomName = Math.random().toString(36).substring(2, 15); // random string
			let tempPath = path.join(tmpDir, randomName);

			await fs.promises.mkdir(tempPath);
			tempPath = path.join(tempPath, "file.nii")

			const ab = await blob.arrayBuffer();
			await fs.promises.writeFile(tempPath, Buffer.from(ab));

			return tempPath;
		} else {
			return { data: blob, name: "file.nii" };
		}
	}

	private async niftiBlobFilterCleanup(input: string | NamedBlob) {
		const isNode = typeof process !== 'undefined' && process.versions?.node;

		if (isNode) {
			const fs = await import('fs');
			const path = await import('path');

			// Recursively delete the directory
			await fs.promises.rm(path.dirname(input as string), { recursive: true, force: true });
		}
	}

	private async convertToTargetDicom(input: NamedBlob | string, cdssApp: CdssModel, originalTags: CdssPatientData, region: CdssBodyRegion,
		seriesDescription: string, uniqueSubmissionId: string, formatDynamicString: FormatDynamicStringCb) {
		const originalSeriesData = originalTags.originalSeriesData?.[region.secondaryCapture.referencedRole];

		const softwareVersions: string[] = [];
		if (cdssApp.appVersion)
			softwareVersions.push(cdssApp.appVersion);

		switch (region.secondaryCapture.modality) {
			case "SEG":
				return await this.dicomService.niftiToDicom(
					input,
					region.secondaryCapture.modality,
					region.niceName,
					this.tagsForModality(
						region.id,
						region.niceName || region.id,
						region.secondaryCapture.modality, originalTags,
						seriesDescription,
						uniqueSubmissionId,
						parseRGBColor(region.secondaryCapture?.segmentColor),
						originalSeriesData,
						softwareVersions,
					),
					region.voiHandling === "split",
					originalTags.originalPatientModuleData ? base64.decode(originalTags.originalPatientModuleData) : undefined,
					region.voiHandling === "pre-split",
					formatDynamicString,
				);
			case "RTSTRUCT":
				if (!originalSeriesData) {
					throw new Error(`Referenced role ${region.secondaryCapture.referencedRole} not found in original series data`);
				}

				const dcmData = await this.dicomService.niftiToRtstruct(input, originalSeriesData.patientMatrix,
					originalSeriesData.sopClass,
					originalSeriesData.instanceUids, seriesDescription,
					parseRGBColor(region.secondaryCapture.segmentColor),
					originalSeriesData.frameOfReferenceUID,
					this.tagsForModality(
						region.id,
						region.niceName || region.id,
						"RTSTRUCT", originalTags,
						seriesDescription,
						uniqueSubmissionId,
						undefined, // No segment color for RTSTRUCT, contour colors are handled in DcmLib
						originalSeriesData,
						softwareVersions,
					),
					originalTags.originalPatientModuleData ? base64.decode(originalTags.originalPatientModuleData) : undefined,
					region.voiHandling === "pre-split",
					formatDynamicString,
				);
				return [{
					data: dcmData,
					name: "rtstruct.dcm",
				}];
			default:
				throw new Error(`Unsupported modality for export: ${region.secondaryCapture.modality}`);
		}
	}
}

export class BatchRemovedError extends Error {
	constructor(public originalError: Error) {
		super("Batch removed");
		this.name = "BatchRemovedError";
	}
}

export interface DynamicStringContext {
  allFeatures: CdssAllFeatures;
}

interface DynamicStringContextInternal extends DynamicStringContext {
	index: number;
}

export function parseRGBColor(color: string): [number, number, number] {
	const match = color?.match(/^#?([a-fA-F0-9]{2})([a-fA-F0-9]{2})([a-fA-F0-9]{2})$/);
	if (!match) {
		return [1, 0, 0]; // Default to red if parsing fails
	}
	return [parseInt(match[1], 16) / 255, parseInt(match[2], 16) / 255, parseInt(match[3], 16) / 255];
}

export function RGBToLab(r: number, g: number, b: number): number[] {
  const xyz = RGBToXYZ(r, g, b);
  return XYZToLab(xyz[0], xyz[1], xyz[2]);
}

// sRGB XYZ points to CIE L*a*b*
// https://en.wikipedia.org/wiki/CIELAB_color_space#CIE_L*a*b
function XYZToLab(x: number, y: number, z: number)
{
  const ref_X = 0.9505;
  const ref_Y = 1.000;
  const ref_Z = 1.089;
  let var_X = x / ref_X;
  let var_Y = y / ref_Y;
  let var_Z = z / ref_Z;

  if ( var_X > 0.008856 ) { var_X = Math.pow(var_X, 1.0/3.0); }
  else                    { var_X = ( 7.787 * var_X ) + ( 16.0 / 116.0 ); }
  if ( var_Y > 0.008856 ) { var_Y = Math.pow(var_Y, 1.0/3.0); }
  else                    { var_Y = ( 7.787 * var_Y ) + ( 16.0 / 116.0 ); }
  if ( var_Z > 0.008856 ) { var_Z = Math.pow(var_Z, 1.0/3.0); }
  else                    { var_Z = ( 7.787 * var_Z ) + ( 16.0 / 116.0 ); }

  const rv: number[] = Array(3);
  rv[0] = ( 116 * var_Y ) - 16;
  rv[1] = 500 * ( var_X - var_Y );
  rv[2] = 200 * ( var_Y - var_Z );
  return rv;
}

// RGB to XYZ sRGB points
function RGBToXYZ(r: number, g: number, b: number) {
  if ( r > 0.04045 ) { r = Math.pow(( r + 0.055 ) / 1.055, 2.4); }
  else               { r = r / 12.92; }
  if ( g > 0.04045 ) { g = Math.pow(( g + 0.055 ) / 1.055, 2.4); }
  else               { g = g / 12.92; }
  if ( b > 0.04045 ) { b = Math.pow(( b + 0.055 ) / 1.055, 2.4); }
  else               { b = b / 12.92; }

  const rv: number[] = Array(3);
  rv[0] = r * 0.4124 + g * 0.3576 + b * 0.1805;
  rv[1] = r * 0.2126 + g * 0.7152 + b * 0.0722;
  rv[2] = r * 0.0193 + g * 0.1192 + b * 0.9505;
  return rv;
}

// https://dicom.nema.org/medical/dicom/current/output/chtml/part03/sect_C.10.7.html#sect_C.10.7.1.1
export function LabToDICOM(l: number, a: number, b: number) {
	const rv: number[] = Array(3);
	rv[0] = Math.round(l / 100 * 0xffff);
	rv[1] = Math.round((a + 128) / 255 * 0xffff);
	rv[2] = Math.round((b + 128) / 255 * 0xffff);
	return rv;
}

async function isDicomFile(blob: Blob): Promise<boolean> {
	try {
		const slice = blob.slice(0, 132);
		const buffer = await slice.arrayBuffer();
		const arr = new Uint8Array(buffer);
		
		// Check for "DICM" at byte offset 128
		return arr.length >= 132 && 
			   arr[128] === 0x44 && 
			   arr[129] === 0x49 && 
			   arr[130] === 0x43 && 
			   arr[131] === 0x4D;
	} catch (error) {
		throw error;
	}
}
